import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Dimmer, Grid, Loader } from "semantic-ui-react";

import { useGetProductById } from "../../../api/product";
import {
  useGetAverageProductSummary,
  useGetProductSummary,
  useGetProductSummaryHistoryDropDownData
} from "../../../api/productSummary";
import { CommonModal, DropDown, InputText, TabView } from "../../../components";
import CommonSplitContainer from "../../../components/commonSplitContainer/CommonSplitContainer";
import MainBottomButtonView from "../../../components/mainBottomButtonView/MainBottomButtonView";
import { VersionHistoryPanel } from "../../../components/drawing-tool/version-history";
import {
  DISABLE_IS_ROOT_PRODUCT_BUTTON,
  PRODUCT_TAB_KEYS,
  ProductViewTabs
} from "../../../config/constants";
import { IsRootProduct } from "../../../config/enums/ProductType.enum";
import ProductDetailSummery from "./productViewTab/ProductDetailsSummery";
import ProductImpactTab from "./productViewTab/ProductImpactTab";
import ProductVolumeAndCost from "./productViewTab/ProductVolumeAndCost";

import "./productDetails.scss";

const ProductDetailModal = ({ visibleModal, setVisibleModal, productId, openEditDetails }: any) => {
  const [selectedTab, setSelectedTab] = useState<Number>(0);
  const [defaultSalesUnit, setDefaultSalesUnit] = useState<string>("");

  const { data: productDetails, isLoading: productDetailsLoading } = useGetProductById(productId);

  const productIsAverage = !DISABLE_IS_ROOT_PRODUCT_BUTTON
    ? productDetails?.is_average_product || false
    : false;
  const productIsAverageResult = productIsAverage ? IsRootProduct.YES : IsRootProduct.NO;

  const productSummaryData = useGetProductSummary({
    productId,
    salesUnitId: defaultSalesUnit,
    productIsAverage
  });
  const { data: productSummaryDropdownData, isFetching: isProductSummaryHistoryLoading } =
    useGetProductSummaryHistoryDropDownData({
      productId,
      salesUnitId: defaultSalesUnit,
      productIsAverage
    });

  const productAverageSummaryData = useGetAverageProductSummary({
    productId,
    productIsAverage: productIsAverage
  });

  const values = useMemo(() => {
    return {
      product_name: productDetails?.product_name,
      registration_number: productDetails?.registration_number,
      product_other_info: productDetails?.product_other_info,
      product_type: productDetails?.product_type
    };
  }, [productDetails]);

  const currentSalesUnitData = useMemo(() => {
    if (productDetails) {
      let salesUnitData = productDetails?.sales_units.reduce((currentType: any, data: any) => {
        let salesUnit = {
          key: data?._id,
          text: data?.sales_unit_name,
          value: data?._id
        };
        return [...currentType, salesUnit];
      }, []);
      return salesUnitData;
    }
    return [];
  }, [productDetails]);

  const selectedUnitSize = useMemo(() => {
    if (productDetails && defaultSalesUnit) {
      const selectedUnit = productDetails?.sales_units.find(
        (unit: any) => unit._id === defaultSalesUnit
      );
      return parseFloat(selectedUnit?.sales_unit_size) || 1;
    }
    return 1;
  }, [productDetails, defaultSalesUnit]);

  const selectedUnitType = useMemo(() => {
    if (productDetails && defaultSalesUnit) {
      const selectedUnit = productDetails?.sales_units.find(
        (unit: any) => unit._id === defaultSalesUnit
      );
      return selectedUnit?.sales_unit_type || "kg";
    }
    return "kg";
  }, [productDetails, defaultSalesUnit]);

  useEffect(() => {
    setDefaultSalesUnit(productDetails?.sales_units[0]._id);
  }, [productDetails]);

  const changeSalesUnitId = (value: string) => {
    setDefaultSalesUnit(value);
  };

  const {
    register,
    formState: { errors }
  } = useForm({
    mode: "all",
    defaultValues: values,
    values: values
  });

  const renderTabContent = () => {
    switch (selectedTab) {
      case PRODUCT_TAB_KEYS.SUMMARY:
        return (
          <ProductDetailSummery
            productDetails={productDetails}
            defaultSalesUnit={defaultSalesUnit}
            productIsAverage={productIsAverageResult}
            productIsAverageStatus={productIsAverage}
          />
        );
      case PRODUCT_TAB_KEYS.ENVIRONMENTAL_FOOTPRINT:
        return (
          <ProductImpactTab
            productDetails={productDetails}
            productSummaryDropdownData={productSummaryDropdownData}
            isProductSummaryHistoryLoading={isProductSummaryHistoryLoading}
          />
        );
      case PRODUCT_TAB_KEYS.HISTORY:
        return (
          <VersionHistoryPanel
            productId={productId}
            salesUnitId={defaultSalesUnit}
            isAverageProduct={productIsAverage}
            onVersionRestored={() => {
              productSummaryData.refetch();
              if (productIsAverage) {
                productAverageSummaryData.refetch();
              }
            }}
            onVersionMetadataChanged={() => productSummaryData.refetch()}
          />
        );
      case PRODUCT_TAB_KEYS.ANNUAL_VOLUME_AND_COST:
        return (
          <ProductVolumeAndCost
            productId={productId}
            salesUnitId={defaultSalesUnit}
            unitSize={selectedUnitSize}
            unitType={selectedUnitType}
          />
        );
      default:
        return null;
    }
  };

  if (productDetailsLoading || productSummaryData.isFetching) {
    return (
      <Dimmer active>
        <Loader content="Loading" />
      </Dimmer>
    );
  }

  return (
    <>
      <CommonModal
        modalOpenState={visibleModal}
        modalCloseState={() => setVisibleModal(false)}
        centered={true}
        title="Product Details"
        contentClassName="sm:!tw-max-w-7xl"
      >
        <Grid>
          <Grid.Column computer={16} tablet={16} mobile={16} className="p-0">
            <div className="productDetailsInsert">
              <form>
                <CommonSplitContainer
                  topChild={
                    <>
                      <Grid className="productDetailsViewMain mb-1">
                        <Grid.Column
                          computer={8}
                          tablet={16}
                          mobile={16}
                          className="paddingRemoveBottom"
                        >
                          <InputText
                            register={register}
                            errors={errors.product_name}
                            labelName={"Product Name"}
                            placeholder="Name"
                            name="product_name"
                            disabled={true}
                          />
                        </Grid.Column>
                        <Grid.Column
                          computer={8}
                          tablet={16}
                          mobile={16}
                          className="paddingRemoveBottom"
                        >
                          <DropDown
                            required={true}
                            labelName={"Sales Unit"}
                            currentData={currentSalesUnitData}
                            defaultValue={defaultSalesUnit}
                            customGridColumn={"customGridColomnType"}
                            handleChangeState={(e: any, { value }: any) => {
                              changeSalesUnitId(value);
                            }}
                          />
                        </Grid.Column>
                      </Grid>
                      <TabView
                        tabData={ProductViewTabs}
                        computer={2}
                        tablet={16}
                        mobile={16}
                        defaultSelect={selectedTab}
                        customMainTab="ml-0"
                        disable={false}
                        onChange={(index: number) => setSelectedTab(index)}
                      />
                      {renderTabContent()}
                    </>
                  }
                  bottomChild={
                    <MainBottomButtonView
                      cancelButtonStatus
                      saveButtonStatus
                      saveTitle={"Edit"}
                      cancelButton={() => setVisibleModal(false)}
                      saveButton={() => {
                        openEditDetails(true);
                      }}
                    />
                  }
                />
              </form>
            </div>
          </Grid.Column>
        </Grid>
      </CommonModal>
    </>
  );
};

export default ProductDetailModal;
